import { useEffect, useRef } from 'react';
import localStorageService from 'src/localStorage';
import {
  getAppLocationInfo,
  getAppSearchParams,
  setAppRouteParams,
} from 'src/workspace/utils';

/**
 * Extract filter values from URL params
 */
function extractFiltersFromUrl<T>(
  urlParams: Record<string, string>,
  prefix: string,
): Partial<T> {
  const filters: Partial<T> = {};

  Object.entries(urlParams).forEach(([key, value]) => {
    if (key.startsWith(prefix)) {
      const filterKey = key.replace(prefix, '');
      try {
        filters[filterKey as keyof T] = JSON.parse(value);
      } catch {
        filters[filterKey as keyof T] = value as T[keyof T];
      }
    }
  });

  return filters;
}

/**
 * Sync filters to URL query params
 */
function syncFiltersToUrl<T extends Record<string, any>>(
  key: string,
  filters: T,
): void {
  const { hash, query } = getAppLocationInfo();
  const params = query ? new URLSearchParams(query) : new URLSearchParams();
  const prefix = `${key}_`;

  Array.from(params.keys())
    .filter((k) => k.startsWith(prefix))
    .forEach((k) => params.delete(k));

  if (Object.keys(filters).length > 0) {
    Object.entries(filters).forEach(([filterKey, value]) => {
      const paramKey = `${prefix}${filterKey}`;
      const paramValue =
        typeof value === 'object' ? JSON.stringify(value) : String(value);
      params.set(paramKey, paramValue);
    });
  }

  setAppRouteParams(true, hash, params.toString());
}

/**
 * Hook to persist filters to localStorage and URL
 *
 * @param key - Unique key for storing filters (e.g., 'homeDashboard')
 * @param filters - Current filter state object
 * @param setFilters - Callback to update filters when restoring
 */
export function useFilterPersistence<T extends Record<string, any>>(
  key: string,
  filters: T,
  setFilters: (filters: T) => void,
): void {
  const storageKey = `filters_${key}`;
  const urlPrefix = `${key}_`;
  const isRestored = useRef(false);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isRestored.current) return;

    // Check URL for filters
    const urlParams = getAppSearchParams();
    const urlFilters = extractFiltersFromUrl<T>(urlParams, urlPrefix);

    if (Object.keys(urlFilters).length > 0) {
      setFilters(urlFilters as T);
    } else {
      // Fallback to localStorage
      const savedFilters = localStorageService.get<T>(storageKey);
      if (savedFilters) {
        setFilters(savedFilters);
      }
    }

    isRestored.current = true;
    isInitialMount.current = false;
  }, [key, storageKey, urlPrefix, setFilters]);

  // Save when filters change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current || !isRestored.current) return;

    // Save to localStorage
    if (Object.keys(filters).length > 0) {
      localStorageService.set(storageKey, filters);
    } else {
      localStorageService.delete(storageKey);
    }

    syncFiltersToUrl(key, filters);
  }, [key, storageKey, filters]);
}
